import type { ClaimIntakeSession } from './claimIntakeAgentApi';
import {
  rumSessionIdYieldAttributes,
  trackClaimIntake,
} from '../rum/rumOperationTracker';

export function claimIntakeSessionStartAttributes(
  session: ClaimIntakeSession | null | undefined,
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  return {
    aiworker_id: session?.aiworkerId ?? null,
    session_id: session?.sessionId ?? null,
    ...(extra ?? {}),
  };
}

export function pickClaimIntakeYieldAttributes(params: {
  aiworkerUsageId?: string | null;
  servicerClassification?: string | null;
  claimNumber?: string | null;
  dummyClaimId?: string | null;
  messageId?: string | null;
  hasWorkflowOutput?: boolean;
}): Record<string, unknown> {
  return {
    aiworker_usage_id: params.aiworkerUsageId ?? null,
    servicer_classification: params.servicerClassification ?? null,
    claim_number: params.claimNumber ?? null,
    dummy_claim_id: params.dummyClaimId ?? null,
    message_id: params.messageId ?? null,
    data_yielded: params.hasWorkflowOutput !== false,
  };
}

/** Summarize bootstrap readiness without leaking PII. */
export function buildClaimIntakePrerequisiteSnapshot(params: {
  tokenReady: boolean;
  agentsReady: boolean;
  actionsReady: boolean;
  claimIntakeAiworkerId: string;
  lookupSourceId: string;
  submitSourceId: string;
  sessionReady: boolean;
}): { ready: boolean; missing: string[] } {
  const missing: string[] = [];
  if (!params.tokenReady) missing.push('auth_token');
  if (!params.agentsReady) missing.push('agent_workers_query');
  if (!params.actionsReady) missing.push('action_sources_query');
  if (!params.claimIntakeAiworkerId) missing.push('claim_intake_aiworker_id');
  if (!params.lookupSourceId) missing.push('lookup_source_id');
  if (!params.submitSourceId) missing.push('submit_source_id');
  if (!params.sessionReady) missing.push('start_stream_session');
  return { ready: missing.length === 0, missing };
}

export function trackClaimIntakeStartStream(params: {
  aiworkerId: string;
  run: () => Promise<ClaimIntakeSession>;
}): Promise<ClaimIntakeSession> {
  return trackClaimIntake({
    operation: 'start_stream',
    startAttributes: { aiworker_id: params.aiworkerId },
    run: params.run,
    completeAttributes: (session) => ({
      ...claimIntakeSessionStartAttributes(session),
      ...rumSessionIdYieldAttributes(session.sessionId),
    }),
  });
}

export function trackClaimIntakeDifyStream<T>(params: {
  session: ClaimIntakeSession;
  event: string;
  aiworkerUsageId?: string | null;
  extraStartAttributes?: Record<string, unknown>;
  run: () => Promise<T>;
  completeAttributes: (result: T) => Record<string, unknown>;
}): Promise<T> {
  return trackClaimIntake({
    operation: 'dify_stream',
    event: params.event,
    startAttributes: {
      ...claimIntakeSessionStartAttributes(params.session),
      aiworker_usage_id: params.aiworkerUsageId ?? null,
      has_chained_usage_id: Boolean(params.aiworkerUsageId),
      ...(params.extraStartAttributes ?? {}),
    },
    run: params.run,
    completeAttributes: params.completeAttributes,
  });
}

export type PresignedUrlEntry = Readonly<{
  presigned_url?: string;
  source_id?: string;
  [key: string]: unknown;
}>;

export type ClaimIntakeUploadPurpose = 'estimate_parser' | 'additional_info' | 'existing_claim_docs';

/** Presigned URL + S3 upload with paired RUM invoked/completed. */
export async function uploadClaimIntakeFilesViaPresigned<TUrlList extends PresignedUrlEntry[]>(params: {
  files: File[];
  purpose: ClaimIntakeUploadPurpose;
  sessionId?: string | null;
  aiworkerUsageId?: string | null;
  getPresignedUrls: (payload: { filename: string; source_type: string }[]) => Promise<TUrlList>;
  uploadOne: (file: File, presignedUrl: string) => Promise<unknown>;
}): Promise<{ urlList: TUrlList; files: File[] }> {
  const { files, purpose } = params;
  if (files.length === 0) {
    return { urlList: [] as unknown as TUrlList, files: [] };
  }

  return trackClaimIntake({
    operation: 'file_upload',
    event: purpose,
    startAttributes: {
      file_count: files.length,
      session_id: params.sessionId ?? null,
      aiworker_usage_id: params.aiworkerUsageId ?? null,
    },
    run: async () => {
      const presignedPayload = files.map((file) => ({
        filename: file.name,
        source_type: 'action',
      }));
      const urlList = await params.getPresignedUrls(presignedPayload);

      await Promise.all(
        files.map((file, index) => {
          const presignedUrl = urlList[index]?.presigned_url;
          if (!presignedUrl) {
            throw new Error('Failed to get upload URL.');
          }
          return params.uploadOne(file, presignedUrl);
        }),
      );

      return { urlList, files };
    },
    completeAttributes: (result) => ({
      file_count: result.files.length,
      source_ids: result.urlList
        .map((entry) => entry.source_id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
      data_yielded: true,
    }),
  });
}
